import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Circle, Path, Line, Text } from 'fabric';
import {
  pointsToBezierPath,
  createBezierCurve,
  calculateDistance,
  isNearPoint,
  applyHierarchicalSnap,
  ENDPOINT_SNAP_DISTANCE,
} from './BezierTool';
import { useCommand } from '../../../context/CommandContext';
import { useGardenSettings } from '../../../context/GardenSettingsContext';
import { CreateObjectCommand } from '../../../commands/CreateObjectCommand';
import { useTouchDeferredVertex } from './useTouchDeferredVertex';
import DrawingToolActions from './DrawingToolActions';

/**
 * Bezier Drawing Tool Component
 *
 * Allows users to draw Bezier curves by clicking to place control points.
 * Supports snap-to-grid, snap-to-endpoint, auto-close, and undo/cancel.
 *
 * Features:
 * - Hierarchical snapping (endpoint → grid → raw)
 * - Minimum 4 control points (cubic Bezier)
 * - Edge labels in meters (using calibration)
 * - Visual feedback (control point markers, preview curve, snap indicators)
 * - Keyboard shortcuts (Escape, Enter, Ctrl)
 * - Command Pattern integration for undo/redo
 * - Crosshair cursor during drawing
 * - Instruction tooltip with point count
 *
 * Note: This component is self-contained and renders its own tooltip,
 * unlike the original implementation which used onStateChange callback
 * that caused infinite re-render loops.
 */
const BezierDrawingTool = ({
  canvas,
  gridSize,
  snapEnabled,
  selectedObjectType,
  onComplete,
  onCancel,
}) => {
  const [controlPoints, setControlPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mousePos, setMousePos] = useState(null);
  const [mousePosScreen, setMousePosScreen] = useState(null); // Screen coordinates for tooltip
  const [ctrlPressed, setCtrlPressed] = useState(false);

  const { commandStack } = useCommand();
  const { settings } = useGardenSettings();

  // Refs for visual objects
  const controlPointMarkersRef = useRef([]);
  const edgeLabelsRef = useRef([]);
  const previewCurveRef = useRef(null);
  // Snap indicator lives in a ref, not in state: mouse:move fires far more often
  // than React re-renders, so a state-held reference is stale for every move
  // within the same render batch — the old indicator never gets removed and
  // orange rings pile up on the canvas (#682).
  const snapIndicatorRef = useRef(null);

  // Constants
  const CLOSE_PROXIMITY = 10; // Pixels to auto-close
  const FIRST_POINT_SIZE = 8;
  const CONTROL_POINT_SIZE = 6;
  const MIN_CONTROL_POINTS = 3; // Minimum for a closed curved shape

  // Get calibration for edge labels (pixels per meter)
  const pixelsPerMeter = settings?.backgroundImage?.calibration?.scale || null;

  /**
   * Format distance for edge labels (meters if calibrated, cm otherwise)
   */
  const formatDistance = useCallback((distanceInPixels) => {
    if (pixelsPerMeter && pixelsPerMeter > 0) {
      const meters = distanceInPixels / pixelsPerMeter;
      return `${meters.toFixed(2)}m`;
    }
    // Fallback: treat pixels as cm (1 pixel = 1 cm)
    return `${distanceInPixels.toFixed(0)}cm`;
  }, [pixelsPerMeter]);

  /**
   * Remove the current snap indicator, if any.
   */
  const removeSnapIndicator = useCallback(() => {
    if (snapIndicatorRef.current) {
      canvas?.remove(snapIndicatorRef.current);
      snapIndicatorRef.current = null;
    }
  }, [canvas]);

  /**
   * Clear all visual feedback objects
   */
  const clearVisuals = useCallback(() => {
    if (!canvas) return;

    controlPointMarkersRef.current.forEach(marker => canvas.remove(marker));
    edgeLabelsRef.current.forEach(label => canvas.remove(label));

    if (previewCurveRef.current) {
      canvas.remove(previewCurveRef.current);
      previewCurveRef.current = null;
    }

    removeSnapIndicator();

    controlPointMarkersRef.current = [];
    edgeLabelsRef.current = [];

    // Safety net: drop anything still flagged temporary. Only one drawing tool
    // is mounted at a time, so every remaining marker belongs to this run —
    // without the sweep a leaked helper stays on the canvas and gets persisted.
    canvas.getObjects()
      .filter(obj => obj.data?.isTemporary)
      .forEach(obj => canvas.remove(obj));

    canvas.requestRenderAll();
  }, [canvas, removeSnapIndicator]);

  /**
   * Render control point markers
   */
  const renderVertices = useCallback(() => {
    // Remove old markers
    controlPointMarkersRef.current.forEach(marker => canvas?.remove(marker));
    controlPointMarkersRef.current = [];

    controlPoints.forEach((point, index) => {
      const isFirst = index === 0;

      const marker = new Circle({
        left: point.x,
        top: point.y,
        radius: isFirst ? FIRST_POINT_SIZE : CONTROL_POINT_SIZE,
        fill: isFirst ? '#10B981' : '#FFFFFF',
        stroke: '#10B981',
        strokeWidth: 2,
        selectable: false,
        evented: false,
        originX: 'center',
        originY: 'center',
        data: { isTemporary: true },
      });

      canvas?.add(marker);
      controlPointMarkersRef.current.push(marker);
    });
  }, [canvas, controlPoints]);

  /**
   * Render edge labels (straight line distances between consecutive points)
   */
  const renderEdges = useCallback(() => {
    if (!canvas) return;

    // Remove old labels
    edgeLabelsRef.current.forEach(label => canvas.remove(label));
    edgeLabelsRef.current = [];

    // Add labels for edges
    for (let i = 0; i < controlPoints.length - 1; i++) {
      const start = controlPoints[i];
      const end = controlPoints[i + 1];
      const distance = calculateDistance(start, end);
      const formattedDistance = formatDistance(distance);

      const label = new Text(formattedDistance, {
        left: (start.x + end.x) / 2,
        top: (start.y + end.y) / 2,
        fontSize: 12,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 'bold',
        fill: '#10B981',
        backgroundColor: '#FFFFFF',
        padding: 3,
        selectable: false,
        evented: false,
        originX: 'center',
        originY: 'center',
        data: { isTemporary: true, isMeasurementLabel: true },
      });

      canvas.add(label);
      edgeLabelsRef.current.push(label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, controlPoints]);

  /**
   * Render preview curve (from current points to mouse position)
   */
  const renderPreview = useCallback(() => {
    if (!canvas) return;

    // Remove old preview
    if (previewCurveRef.current) {
      canvas.remove(previewCurveRef.current);
      previewCurveRef.current = null;
    }

    // Need at least 1 point to show preview
    if (!mousePos || controlPoints.length < 1) {
      return;
    }

    // Create preview with current mouse position
    const previewPoints = [...controlPoints, mousePos];
    const lastPoint = controlPoints[controlPoints.length - 1];
    const isNearFirst = controlPoints.length >= MIN_CONTROL_POINTS && isNearPoint(mousePos, controlPoints[0], CLOSE_PROXIMITY);

    // Show curve preview if we have at least 2 points (will become 3 with mouse)
    if (previewPoints.length >= 3) {
      try {
        const pathData = pointsToBezierPath(previewPoints, false); // Don't close yet
        const preview = new Path(pathData, {
          originX: 'left',
          originY: 'top',
          stroke: isNearFirst ? '#FFA500' : '#10B981',
          strokeWidth: 2,
          strokeDashArray: isNearFirst ? [] : [5, 5], // Solid when near first, dashed otherwise
          fill: 'rgba(16, 185, 129, 0.1)',
          selectable: false,
          evented: false,
          data: { isTemporary: true },
        });

        canvas.add(preview);
        previewCurveRef.current = preview;
      } catch (error) {
        // Ignore errors during preview (e.g., invalid path data)
        console.warn('Preview error:', error);
      }
    } else {
      // Show line preview when we have less than 2 points
      previewCurveRef.current = new Line(
        [lastPoint.x, lastPoint.y, mousePos.x, mousePos.y],
        {
          originX: 'left',
          originY: 'top',
          stroke: '#10B981',
          strokeWidth: 2,
          strokeDashArray: [5, 5],
          selectable: false,
          evented: false,
          data: { isTemporary: true },
        }
      );

      canvas.add(previewCurveRef.current);
    }
  }, [canvas, mousePos, controlPoints]);

  /**
   * Render snap indicator (circle around snap target)
   */
  const renderSnapIndicator = useCallback((point, target) => {
    // Remove old indicator
    removeSnapIndicator();

    if (!target) return;

    const indicator = new Circle({
      left: point.x - 6,
      top: point.y - 6,
      originX: 'left',
      originY: 'top',
      radius: 6,
      fill: 'transparent',
      stroke: '#FFA500', // Orange for endpoint snap
      strokeWidth: 2,
      selectable: false,
      evented: false,
      data: { isTemporary: true },
    });

    canvas.add(indicator);
    snapIndicatorRef.current = indicator;
  }, [canvas, removeSnapIndicator]);

  /**
   * Update all visuals
   */
  useEffect(() => {
    if (!canvas) return;

    // eslint-disable-next-line react-hooks/immutability -- imperative Fabric render-batching flag; restored to true two lines below
    canvas.renderOnAddRemove = false;
    renderVertices();
    renderEdges();
    renderPreview();
    canvas.renderOnAddRemove = true;
    canvas.renderAll();
  }, [canvas, renderVertices, renderEdges, renderPreview]);

  /**
   * Set crosshair cursor
   */
  useEffect(() => {
    if (!canvas) return;

    // eslint-disable-next-line react-hooks/immutability -- imperative Fabric cursor config; reverted in cleanup
    canvas.defaultCursor = 'crosshair';
    canvas.setCursor('crosshair');

    return () => {
      canvas.defaultCursor = 'default';
      canvas.setCursor('default');
    };
  }, [canvas]);

  /**
   * Finalize the (closed) curve: build the Fabric object via the undo/redo
   * command, clear the in-progress visuals and reset drawing state. Shared by the
   * auto-close (tap near first point), the Enter key, and the touch "Fertig"
   * button (#747).
   */
  const finalizeCurve = useCallback((points) => {
    clearVisuals();

    const pathData = pointsToBezierPath(points, true); // true = close path
    const bezierCurve = createBezierCurve(pathData, {
      data: {
        type: 'bezier',
        isActive: true,
        createdAt: new Date().toISOString(),
        controlPoints: points,
      },
    }, selectedObjectType);

    const command = new CreateObjectCommand(canvas, bezierCurve);
    commandStack.execute(command);

    setControlPoints([]);
    setIsDrawing(false);
    setMousePos(null);
    setMousePosScreen(null);
    onComplete?.(bezierCurve);
  }, [canvas, clearVisuals, commandStack, selectedObjectType, onComplete]);

  /**
   * Commit a control point at the given (already snapped) point. Shared by the
   * mouse path (mouse:down) and the touch path (mouse:up, via
   * useTouchDeferredVertex). Closes the curve when placed near the first point.
   */
  const commitPoint = useCallback((snappedPoint) => {
    if (
      controlPoints.length >= MIN_CONTROL_POINTS &&
      isNearPoint(snappedPoint, controlPoints[0], CLOSE_PROXIMITY)
    ) {
      finalizeCurve(controlPoints);
      return;
    }

    setControlPoints(prev => [...prev, snappedPoint]);
    setIsDrawing(true);
  }, [controlPoints, finalizeCurve]);

  // Clear the preview when a pinch cancels a pending touch point (#747).
  const clearTouchPreview = useCallback(() => setMousePos(null), []);
  const { deferIfTouch, updatePoint } = useTouchDeferredVertex(canvas, commitPoint, clearTouchPreview);

  /**
   * Handle mouse move (update preview)
   */
  useEffect(() => {
    if (!canvas) return;

    const handleMouseMove = (e) => {
      const pointer = canvas.getScenePoint(e.e);

      // Apply hierarchical snap
      const snapResult = applyHierarchicalSnap(
        pointer,
        canvas,
        gridSize,
        snapEnabled,
        ctrlPressed,
        ENDPOINT_SNAP_DISTANCE
      );

      setMousePos(snapResult.point);
      updatePoint(snapResult.point); // #747: keep the touch commit-on-lift target current
      setMousePosScreen({ x: e.e.clientX, y: e.e.clientY }); // Store screen coords for tooltip

      // Show snap indicator for endpoint snap
      if (snapResult.snappedTo === 'endpoint') {
        renderSnapIndicator(snapResult.point, snapResult.target);
      } else {
        removeSnapIndicator();
      }
    };

    canvas.on('mouse:move', handleMouseMove);
    return () => canvas.off('mouse:move', handleMouseMove);
  }, [canvas, gridSize, snapEnabled, ctrlPressed, renderSnapIndicator, removeSnapIndicator, updatePoint]);

  /**
   * Handle mouse down (add control point or close curve)
   */
  useEffect(() => {
    if (!canvas) return;

    const handleMouseDown = (e) => {
      const pointer = canvas.getScenePoint(e.e);

      // Apply hierarchical snap
      const snapResult = applyHierarchicalSnap(
        pointer,
        canvas,
        gridSize,
        snapEnabled,
        ctrlPressed,
        ENDPOINT_SNAP_DISTANCE
      );

      const snappedPoint = snapResult.point;

      // Touch: defer placement to lift so the point can be dragged into position
      // first — touch has no hover to preview before a tap (#747).
      if (deferIfTouch(e.e, snappedPoint)) {
        setMousePos(snappedPoint);
        return;
      }

      commitPoint(snappedPoint);
    };

    canvas.on('mouse:down', handleMouseDown);
    return () => canvas.off('mouse:down', handleMouseDown);
  }, [canvas, gridSize, snapEnabled, ctrlPressed, commitPoint, deferIfTouch]);

  /**
   * Handle keyboard events
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Enter key - finalize curve (if we have enough points)
      if (e.key === 'Enter' && controlPoints.length >= MIN_CONTROL_POINTS) {
        finalizeCurve(controlPoints);
        e.preventDefault();
        return;
      }

      // Escape key - cancel drawing
      if (e.key === 'Escape' && isDrawing) {
        clearVisuals();
        setControlPoints([]);
        setIsDrawing(false);
        setMousePos(null);
        setMousePosScreen(null);
        onCancel?.();
      }

      // Ctrl key - disable grid snap
      if (e.key === 'Control') {
        setCtrlPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Control') {
        setCtrlPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [controlPoints, isDrawing, clearVisuals, onCancel, finalizeCurve]);

  /**
   * Cleanup when unmounting
   */
  useEffect(() => {
    return () => {
      clearVisuals();
    };
  }, [clearVisuals]);

  // Get instruction text for tooltip
  const getInstructionText = () => {
    if (controlPoints.length < MIN_CONTROL_POINTS) {
      return `Punkt ${controlPoints.length + 1} von ${MIN_CONTROL_POINTS} • ${MIN_CONTROL_POINTS} Punkte mindestens`;
    }
    return `Punkt ${controlPoints.length + 1} • Enter zum Abschließen`;
  };

  // Touch: no Enter/Escape — cancel the whole in-progress curve (#747).
  const handleTouchCancel = useCallback(() => {
    clearVisuals();
    setControlPoints([]);
    setIsDrawing(false);
    setMousePos(null);
    setMousePosScreen(null);
    onCancel?.();
  }, [clearVisuals, onCancel]);

  // Self-contained component with its own tooltip (like PolygonDrawingTool)
  // This avoids the infinite re-render loop caused by onStateChange callback
  return (
    <>
      {/* Instruction tooltip - shows point count and instructions while drawing */}
      {isDrawing && mousePosScreen && (
        <div
          style={{
            position: 'fixed',
            left: `${mousePosScreen.x + 15}px`,
            top: `${mousePosScreen.y + 15}px`,
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <div className="bg-white/95 backdrop-blur-sm border-2 border-emerald-500 rounded shadow-lg px-3 py-2">
            <div className="text-sm font-semibold text-gray-900 text-center">
              {getInstructionText()}
            </div>
            <div className="text-xs text-gray-500 text-center mt-1">
              {controlPoints.length} {controlPoints.length === 1 ? 'Punkt' : 'Punkte'}
            </div>
          </div>
        </div>
      )}
      <DrawingToolActions
        canFinish={controlPoints.length >= MIN_CONTROL_POINTS}
        onFinish={() => finalizeCurve(controlPoints)}
        onCancel={handleTouchCancel}
      />
    </>
  );
};

BezierDrawingTool.propTypes = {
  canvas: PropTypes.object.isRequired,
  gridSize: PropTypes.number.isRequired,
  snapEnabled: PropTypes.bool.isRequired,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};

BezierDrawingTool.defaultProps = {
  onComplete: null,
  onCancel: null,
};

export default BezierDrawingTool;
